import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import {
  AlertTriangle, ArrowUpRight, Calendar, Camera, CheckCircle2, ChevronLeft, ClipboardList, Home,
  MapPin, MoreHorizontal, Phone, Mail, Sparkles, Trash2, Upload, X, Info,
} from 'lucide-react';
import { Project, Photo, Room } from '../../types';
import { createProject, deleteProject, getProject, updateProject } from '../../services/projectService';
import { getRoomsByProject } from '../../services/roomService';
import { getItemsByProject } from '../../services/itemService';
import { getPhotosByProject } from '../../services/photoService';
import { getChecklistsByProject, getChecklistItems } from '../../services/checklistService';
import { getDamageReportsByProject } from '../../services/damageReportService';
import { isAuthenticated, getAvailableTimeSlots } from '../../services/googleCalendarService';
import { fixAddress, polishText } from '../../services/aiAssistService';
import { aggregateLensData, formatSEK } from '../../utils/lensAggregation';
import { lookupPostalCode } from '../../utils/geocoding';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { useFormNavigation } from '../../hooks/useFormNavigation';
import { useNotifications } from '../../contexts/NotificationContext';
import { EVALUATORS, TEAM_MEMBERS } from '../../constants/people';
import CalendarSyncStatus from '../CalendarSyncStatus';
import ConfirmDialog from '../ConfirmDialog';
import UnsavedChangesDialog from '../UnsavedChangesDialog';
import { Button } from '../ui/Button';
import { Field, Input, Select, Textarea } from '../ui/Field';
import { DateField, formatSvDate } from '../ui/DateField';
import { ActionBar } from '../ui/ActionBar';
import { Popover } from '../ui/Popover';
import { Skeleton } from '../ui/Skeleton';
import { getStatusLabel, StatusBadge } from '../ui/StatusBadge';
import { StatusSelect } from './StatusSelect';
import { PhotoPanel } from './PhotoPanel';

type Mode = 'create' | 'edit';

type FormData = {
  address: { street: string; city: string; postalCode: string; accessCode: string; accessInfo: string; coordinates: { lat: number; lng: number } | null };
  contact: { name: string; phone: string; email: string };
  dates: { evaluation: Timestamp | null; evaluationStartTime: string; evaluationEndTime: string; scheduledPickup: Timestamp | null; scheduledPickupTime: string; actualPickup: Timestamp | null };
  status: Project['status'];
  generalNotes: string;
  evaluator: string;
  pickupTeam: string[];
};

const EMPTY_FORM: FormData = {
  address: { street: '', city: '', postalCode: '', accessCode: '', accessInfo: '', coordinates: null },
  contact: { name: '', phone: '', email: '' },
  dates: { evaluation: null, evaluationStartTime: '', evaluationEndTime: '', scheduledPickup: null, scheduledPickupTime: '', actualPickup: null },
  status: 'bostad',
  generalNotes: '',
  evaluator: '',
  pickupTeam: [],
};

const TIME_OPTIONS: string[] = [];
for (let h = 6; h <= 20; h++) {
  TIME_OPTIONS.push(`${String(h).padStart(2, '0')}:00`, `${String(h).padStart(2, '0')}:30`);
}

const toForm = (p: Project): FormData => ({
  address: {
    street: p.address.street, city: p.address.city, postalCode: p.address.postalCode,
    accessCode: p.address.accessCode || '', accessInfo: p.address.accessInfo || '', coordinates: p.address.coordinates,
  },
  contact: { name: p.contact.name, phone: p.contact.phone, email: p.contact.email || '' },
  dates: {
    evaluation: p.dates.evaluation, evaluationStartTime: p.dates.evaluationStartTime || '', evaluationEndTime: p.dates.evaluationEndTime || '',
    scheduledPickup: p.dates.scheduledPickup, scheduledPickupTime: p.dates.scheduledPickupTime || '', actualPickup: p.dates.actualPickup,
  },
  status: p.status,
  generalNotes: p.generalNotes || '',
  evaluator: p.evaluator || '',
  pickupTeam: p.pickupTeam || [],
});

const dateOf = (t: Timestamp | null): Date | null => (t ? t.toDate() : null);
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const isPast = (t: Timestamp | null) => !!t && t.toDate() < startOfToday();

/** Field-level change count for the action bar. */
const countChanges = (a: FormData, b: FormData): number => {
  let n = 0;
  (Object.keys(a.address) as (keyof FormData['address'])[]).forEach(k => { if (JSON.stringify(a.address[k]) !== JSON.stringify(b.address[k])) n++; });
  (Object.keys(a.contact) as (keyof FormData['contact'])[]).forEach(k => { if (a.contact[k] !== b.contact[k]) n++; });
  (Object.keys(a.dates) as (keyof FormData['dates'])[]).forEach(k => {
    const x = a.dates[k]; const y = b.dates[k];
    const xs = x && typeof x === 'object' && 'toDate' in x ? x.toDate().getTime() : x;
    const ys = y && typeof y === 'object' && 'toDate' in y ? y.toDate().getTime() : y;
    if (xs !== ys) n++;
  });
  if (a.status !== b.status) n++;
  if (a.generalNotes !== b.generalNotes) n++;
  if (a.evaluator !== b.evaluator) n++;
  if (JSON.stringify(a.pickupTeam) !== JSON.stringify(b.pickupTeam)) n++;
  return n;
};

const Card: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode; id?: string; className?: string }> = ({ title, action, children, id, className = '' }) => (
  <section id={id} aria-label={title} className={`rounded-ios-card border border-[var(--border-default)] bg-[var(--bg-surface)] ${className}`}>
    <header className="flex items-center justify-between gap-3 px-5 pt-4 pb-3">
      <h2 className="text-[15px] font-semibold text-[var(--text-primary)]">{title}</h2>
      {action}
    </header>
    <div className="px-5 pb-5">{children}</div>
  </section>
);

/**
 * Desktop project workspace: detail and edit on one screen. Every fact of a
 * project is visible, every field edits in place, and one Spara commits.
 * In `create` mode the same layout serves the new-project form.
 */
export function ProjectWorkspace({ mode }: { mode: Mode }) {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isOnline = useOnlineStatus();
  const { addNotification } = useNotifications();

  const [project, setProject] = useState<Project | null>(null);
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [initial, setInitial] = useState<FormData>(EMPTY_FORM);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showDelete, setShowDelete] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [pickupHint, setPickupHint] = useState(false);
  const [aiBusy, setAiBusy] = useState<'address' | 'notes' | null>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const pickupDateRef = useRef<HTMLDivElement>(null);
  const openPicker = useRef<(() => void) | null>(null);

  const [rooms, setRooms] = useState<Room[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [itemCount, setItemCount] = useState(0);
  const [checklist, setChecklist] = useState({ total: 0, done: 0 });
  const [damage, setDamage] = useState({ total: 0, open: 0 });

  const [calendarAuth, setCalendarAuth] = useState(false);
  const [evalSlots, setEvalSlots] = useState<{ time: string; available: boolean }[]>([]);
  const [pickupSlots, setPickupSlots] = useState<{ time: string; available: boolean }[]>([]);
  const [customMember, setCustomMember] = useState('');

  const changes = useMemo(() => countChanges(form, initial), [form, initial]);
  const dirty = mode === 'create' ? true : changes > 0;

  // A draft handed over from the home screen's quick-add popover: whatever
  // was typed there is the starting point here, nothing is retyped.
  useEffect(() => {
    if (mode !== 'create') return;
    const draft = (location.state as { draft?: { street?: string; city?: string; name?: string; phone?: string; date?: string | null } } | null)?.draft;
    if (!draft) return;
    setForm(f => ({
      ...f,
      address: { ...f.address, street: draft.street || '', city: draft.city || '' },
      contact: { ...f.contact, name: draft.name || '', phone: draft.phone || '' },
      dates: { ...f.dates, evaluation: draft.date ? Timestamp.fromDate(new Date(draft.date)) : null },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const load = useCallback(async () => {
    if (mode !== 'edit' || !projectId) { setLoading(false); return; }
    try {
      setLoading(true);
      const p = await getProject(projectId);
      if (!p) { setError('Projektet kunde inte hittas.'); return; }
      setProject(p);
      const f = toForm(p);
      setForm(f); setInitial(f);
      const [r, items, ph, cls, dr] = await Promise.all([
        getRoomsByProject(projectId), getItemsByProject(projectId), getPhotosByProject(projectId),
        getChecklistsByProject(projectId), getDamageReportsByProject(projectId),
      ]);
      setRooms(r); setItemCount(items.length); setPhotos(ph);
      setDamage({ total: dr.length, open: dr.filter(d => d.status === 'open').length });
      if (cls.length > 0) {
        const all = (await Promise.all(cls.map(c => getChecklistItems(c.id)))).flat();
        setChecklist({ total: all.length, done: all.filter(i => i.completed).length });
      }
      setError(null);
    } catch (e) {
      console.error('Workspace load failed:', e);
      setError('Kunde inte hämta projektet. Försök igen.');
    } finally {
      setLoading(false);
    }
  }, [mode, projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { isAuthenticated().then(setCalendarAuth).catch(() => setCalendarAuth(false)); }, []);

  useEffect(() => {
    if (!calendarAuth || !form.dates.evaluation) { setEvalSlots([]); return; }
    const eventId = project ? localStorage.getItem(`evaluation_event_${project.id}`) : null;
    getAvailableTimeSlots(form.dates.evaluation.toDate(), eventId || undefined).then(setEvalSlots).catch(() => setEvalSlots([]));
  }, [calendarAuth, form.dates.evaluation, project]);

  useEffect(() => {
    if (!calendarAuth || !form.dates.scheduledPickup) { setPickupSlots([]); return; }
    const eventId = project ? localStorage.getItem(`pickup_event_${project.id}`) : null;
    getAvailableTimeSlots(form.dates.scheduledPickup.toDate(), eventId || undefined).then(setPickupSlots).catch(() => setPickupSlots([]));
  }, [calendarAuth, form.dates.scheduledPickup, project]);

  const refreshPhotos = useCallback(() => {
    if (!projectId) return;
    getPhotosByProject(projectId).then(setPhotos).catch(err => console.error('Refresh photos failed:', err));
  }, [projectId]);

  // --- field setters -------------------------------------------------------
  const setAddress = (k: keyof FormData['address'], v: string) => setForm(f => ({ ...f, address: { ...f.address, [k]: v } }));
  const setContact = (k: keyof FormData['contact'], v: string) => setForm(f => ({ ...f, contact: { ...f.contact, [k]: v } }));
  const setDates = (patch: Partial<FormData['dates']>) => setForm(f => ({ ...f, dates: { ...f.dates, ...patch } }));

  const setPickupDate = (d: Date | null) => {
    setPickupHint(false);
    setForm(f => {
      const next = { ...f, dates: { ...f.dates, scheduledPickup: d ? Timestamp.fromDate(d) : null, scheduledPickupTime: d ? f.dates.scheduledPickupTime : '' } };
      // The rule the panel states: a pickup date moves a fresh project to Planerad.
      if (d && (f.status === 'bostad' || f.status === 'hem' || f.status === 'värderad')) next.status = 'planerad';
      return next;
    });
  };

  /**
   * Status and dates are the project's spine, so in edit mode they save the
   * moment they change, exactly like the table on the home screen. Everything
   * else (times, people, text) goes through Spara.
   */
  const commitSpine = async (patch: Partial<Project>, label: string) => {
    if (!project) return;
    if (!isOnline) { addNotification({ type: 'error', message: 'Du är offline. Ändringen sparades inte.', duration: 4000 }); return; }
    const previous = project;
    const updated = { ...project, ...patch } as Project;
    setProject(updated);
    const f = toForm(updated);
    // Carry over unsaved non-spine edits so they are not lost by the refresh.
    setForm(cur => ({ ...cur, status: f.status, dates: { ...cur.dates, evaluation: f.dates.evaluation, scheduledPickup: f.dates.scheduledPickup, evaluationStartTime: f.dates.evaluationStartTime, evaluationEndTime: f.dates.evaluationEndTime, scheduledPickupTime: f.dates.scheduledPickupTime } }));
    setInitial(cur => ({ ...cur, status: f.status, dates: { ...cur.dates, evaluation: f.dates.evaluation, scheduledPickup: f.dates.scheduledPickup, evaluationStartTime: f.dates.evaluationStartTime, evaluationEndTime: f.dates.evaluationEndTime, scheduledPickupTime: f.dates.scheduledPickupTime } }));
    try {
      await updateProject(project.id, patch);
      setSavedAt(new Date());
      addNotification({ type: 'success', message: label, duration: 2500 });
    } catch (e) {
      console.error('Spine update failed:', e);
      setProject(previous);
      const back = toForm(previous);
      setForm(cur => ({ ...cur, status: back.status, dates: { ...cur.dates, evaluation: back.dates.evaluation, scheduledPickup: back.dates.scheduledPickup } }));
      setInitial(cur => ({ ...cur, status: back.status, dates: { ...cur.dates, evaluation: back.dates.evaluation, scheduledPickup: back.dates.scheduledPickup } }));
      addNotification({ type: 'error', message: 'Kunde inte spara. Försök igen.', duration: 4000 });
    }
  };

  const commitStatus = (status: Project['status']) =>
    commitSpine({ status }, `Status: ${getStatusLabel(status)}`);

  const commitEvaluationDate = (d: Date | null) => {
    if (!project) return;
    const dates = { ...project.dates, evaluation: d ? Timestamp.fromDate(d) : null, ...(d ? {} : { evaluationStartTime: '', evaluationEndTime: '' }) };
    commitSpine({ dates }, d ? `Hembesök ${formatSvDate(d)} sparat` : 'Hembesöksdatum borttaget');
  };

  const commitPickupDate = (d: Date | null) => {
    if (!project) return;
    setPickupHint(false);
    const dates = { ...project.dates, scheduledPickup: d ? Timestamp.fromDate(d) : null, ...(d ? {} : { scheduledPickupTime: '' }) };
    const patch: Partial<Project> = { dates };
    const moves = !!d && (project.status === 'bostad' || project.status === 'hem' || project.status === 'värderad');
    if (moves) patch.status = 'planerad';
    commitSpine(patch, d ? (moves ? `Hämtning ${formatSvDate(d)} sparat. Status: Hämtning planerad` : `Hämtning ${formatSvDate(d)} sparat`) : 'Hämtningsdatum borttaget');
  };

  const setEvalStart = (v: string) => {
    let end = form.dates.evaluationEndTime;
    if (v) { const [h, m] = v.split(':').map(Number); end = `${String((h + 1) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`; }
    setDates({ evaluationStartTime: v, evaluationEndTime: end });
  };

  const addMember = (name: string) => {
    const n = name.trim();
    if (!n || form.pickupTeam.includes(n)) return;
    setForm(f => ({ ...f, pickupTeam: [...f.pickupTeam, n] }));
    setCustomMember('');
  };

  // --- save / delete -------------------------------------------------------
  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.address.street.trim()) errs['address.street'] = 'Gatuadress krävs';
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError('Gatuadressen saknas. Det är det enda fältet som krävs.');
      const first = document.querySelector<HTMLElement>(`[name="${CSS.escape(Object.keys(errs)[0])}"]`);
      first?.focus();
      return false;
    }
    setError(null);
    return true;
  };

  const save = async (): Promise<string | false> => {
    if (!isOnline) { setError('Du är offline. Anslut för att spara.'); return false; }
    if (!validate()) return false;
    const data = {
      address: { ...form.address, street: form.address.street.trim(), city: form.address.city.trim(), postalCode: form.address.postalCode.trim(), accessCode: form.address.accessCode.trim(), accessInfo: form.address.accessInfo.trim() },
      contact: { name: form.contact.name.trim(), phone: form.contact.phone.trim(), email: form.contact.email.trim() },
      dates: { ...form.dates },
      status: form.status,
      generalNotes: form.generalNotes.trim(),
      evaluator: form.evaluator.trim(),
      pickupTeam: form.pickupTeam,
    };
    try {
      setSaving(true);
      if (mode === 'edit' && project) {
        await updateProject(project.id, data);
        const updated = { ...project, ...data } as Project;
        setProject(updated);
        const f = toForm(updated);
        setForm(f); setInitial(f);
        setSavedAt(new Date());
        addNotification({ type: 'success', message: 'Projektet sparat.', duration: 2500 });
        return project.id;
      }
      const { id } = await createProject(data as any);
      setInitial(form);
      addNotification({ type: 'success', message: 'Projektet skapat.', duration: 2500 });
      return id;
    } catch (e) {
      console.error('Save failed:', e);
      setError('Kunde inte spara projektet. Försök igen.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    const id = await save();
    if (id && mode === 'create') navigate(`/projects/${id}`, { replace: true });
  };

  const { navigateSafely, showDialog, handleSave, handleDiscard, handleContinueEditing, isSaving } = useFormNavigation({
    onSave: save,
    hasUnsavedChanges: mode === 'edit' ? changes > 0 : form.address.street.trim().length > 0,
  });

  const confirmDelete = async () => {
    if (!project) return;
    try {
      setSaving(true);
      await deleteProject(project.id);
      addNotification({ type: 'success', message: 'Projektet flyttat till papperskorgen.', duration: 4000 });
      navigate('/');
    } catch (e) {
      console.error('Delete failed:', e);
      setError('Kunde inte ta bort projektet.');
    } finally {
      setSaving(false); setShowDelete(false);
    }
  };

  const markReviewed = async () => {
    if (!project) return;
    try {
      await updateProject(project.id, { needsReview: false });
      setProject({ ...project, needsReview: false });
    } catch (e) { console.error(e); }
  };

  // --- derived -------------------------------------------------------------
  const lens = useMemo(() => aggregateLensData(photos), [photos]);
  const lensValue = lens.totalMarketValue > 0 ? lens.totalMarketValue : lens.totalEstimatedValue;
  const title = mode === 'create' ? 'Nytt projekt' : form.address.street || 'Projekt';
  const evalPast = isPast(form.dates.evaluation);
  const pickupPast = isPast(form.dates.scheduledPickup);
  // Calendar imports carry arbitrary times (11:40); a select whose value is
  // not among its options shows nothing, so the current value is always listed.
  const withCurrent = (current: string) => (current && !TIME_OPTIONS.includes(current) ? [...TIME_OPTIONS, current].sort() : TIME_OPTIONS);
  const timeOptions = (slots: { time: string; available: boolean }[], date: Timestamp | null, current = '') =>
    withCurrent(current).map(t => {
      const slot = slots.find(s => s.time === t);
      const busy = !!slot && !slot.available;
      let past = false;
      if (date) { const d = date.toDate(); const [h, m] = t.split(':').map(Number); d.setHours(h, m, 0, 0); past = d < new Date(); }
      return { t, label: `${t}${past ? ' (passerad)' : busy ? ' (upptagen)' : ''}` };
    });

  if (loading) {
    return (
      <div className="px-8 py-6 max-w-[1200px] mx-auto space-y-5" aria-busy="true" aria-label="Laddar projekt">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-9 w-80" />
        <div className="grid grid-cols-[minmax(0,1fr)_380px] gap-5">
          <div className="space-y-5"><Skeleton className="h-64 w-full rounded-ios-card" /><Skeleton className="h-40 w-full rounded-ios-card" /></div>
          <div className="space-y-5"><Skeleton className="h-80 w-full rounded-ios-card" /><Skeleton className="h-32 w-full rounded-ios-card" /></div>
        </div>
      </div>
    );
  }

  if (error && mode === 'edit' && !project) {
    return (
      <div className="px-8 py-6 max-w-[1200px] mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><ChevronLeft size={16} /> Projekt</Link>
        <div role="alert" className="mt-4 rounded-ios-card border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">{error}</div>
      </div>
    );
  }

  return (
    <div className="px-8 py-6 max-w-[1200px] mx-auto">
      <ConfirmDialog
        isOpen={showDelete}
        title="Ta bort projekt"
        message="Projektet och alla rum, föremål, foton och skaderapporter flyttas till papperskorgen i 7 dagar. Därefter raderas de."
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        variant="danger"
        loading={saving}
        onConfirm={confirmDelete}
        onCancel={() => setShowDelete(false)}
      />
      <UnsavedChangesDialog isOpen={showDialog} onClose={handleContinueEditing} onDiscard={handleDiscard} onSave={handleSave} isSaving={isSaving} />

      {/* Breadcrumb + header */}
      <nav aria-label="Brödsmulor" className="mb-3">
        <button type="button" onClick={() => navigateSafely('/')} className="inline-flex items-center gap-1 h-[32px] -ml-2 px-2 rounded-ios-control text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)] hover:text-[var(--text-primary)]">
          <ChevronLeft size={16} aria-hidden="true" /> Projekt
        </button>
      </nav>

      <header className="flex items-start justify-between gap-6 mb-6">
        <div className="min-w-0">
          <h1 className="text-[28px] leading-[34px] font-semibold tracking-tight text-[var(--text-primary)] truncate">{title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-[var(--text-secondary)]">
            {mode === 'edit' && <StatusBadge status={form.status} size="md" />}
            {form.address.city && <span>{form.address.postalCode ? `${form.address.postalCode} ` : ''}{form.address.city}</span>}
            {project?.updatedAt && (
              <span className="tabular-nums">
                {savedAt ? `Sparat ${savedAt.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}` : `Uppdaterad ${formatSvDate(project.updatedAt.toDate())}`}
              </span>
            )}
          </div>
        </div>
        {mode === 'edit' && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="secondary" icon={<Upload size={16} />} onClick={() => { openPicker.current?.(); document.getElementById('foton')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }}>
              Ladda upp foton
            </Button>
            <Button ref={moreRef} variant="secondary" aria-label="Fler åtgärder" aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen(o => !o)} className="px-2.5">
              <MoreHorizontal size={18} />
            </Button>
            <Popover open={moreOpen} onClose={() => setMoreOpen(false)} anchorRef={moreRef} align="end" width={240} aria-label="Fler åtgärder">
              <div role="menu" className="py-1.5">
                <a role="menuitem" href={`https://maps.google.com/?q=${encodeURIComponent([form.address.street, form.address.postalCode, form.address.city, 'Sverige'].filter(Boolean).join(', '))}`} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 px-3.5 h-9 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]" onClick={() => setMoreOpen(false)}>
                  <MapPin size={16} className="text-[var(--text-secondary)]" /> Öppna i Google Maps
                </a>
                <Link to={`/projects/${projectId}/rooms`} className="flex items-center gap-2.5 px-3.5 h-9 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]" onClick={() => setMoreOpen(false)}>
                  <Home size={16} className="text-[var(--text-secondary)]" /> Rum & objekt
                </Link>
                <div className="my-1.5 h-px bg-[var(--border-subtle)]" />
                <button role="menuitem" type="button" onClick={() => { setMoreOpen(false); setShowDelete(true); }} className="w-full flex items-center gap-2.5 px-3.5 h-9 text-sm text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20">
                  <Trash2 size={16} /> Ta bort projekt
                </button>
              </div>
            </Popover>
          </div>
        )}
      </header>

      {project?.needsReview && (
        <div className="mb-5 flex items-center justify-between gap-4 rounded-ios-card border border-blue-200 dark:border-blue-800/40 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
          <p className="flex items-center gap-2 text-sm text-blue-900 dark:text-blue-200"><Calendar size={16} aria-hidden="true" /> Importerat från kalendern. Kontrollera adress och kontakt innan du går vidare.</p>
          <Button size="sm" variant="secondary" onClick={markReviewed}>Markera som kontrollerat</Button>
        </div>
      )}

      {error && (
        <div role="alert" className="mb-5 rounded-ios-card border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">{error}</div>
      )}

      <div className="grid grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px] gap-5 items-start">
        {/* Left column */}
        <div className="space-y-5 min-w-0">
          <Card
            title="Adress"
            action={form.address.street.trim().length > 3 && (
              <Button size="sm" variant="ghost" icon={<Sparkles size={14} />} loading={aiBusy === 'address'} onClick={async () => {
                if (!isOnline) { addNotification({ type: 'error', message: 'Du är offline. Adressen kan inte kontrolleras nu.', duration: 4000 }); return; }
                try {
                  setAiBusy('address');
                  const r = await fixAddress(form.address.street, form.address.postalCode, form.address.city, form.address.accessCode, form.address.accessInfo, { name: form.contact.name, phone: form.contact.phone, email: form.contact.email });
                  // The model guesses at postcodes; the map service knows them.
                  const SURE = 0.6;
                  if (r && r.confidence < SURE && !(r.changes && r.changes.length === 0)) {
                    addNotification({ type: 'info', message: 'Osäker tolkning av adressen. Inget ändrades, justera fälten själv.', duration: 5000 });
                    return;
                  }
                  let postalCode = (r && r.confidence >= SURE && r.postalCode) || form.address.postalCode;
                  let postalFromMap = false;
                  if (!postalCode.trim()) {
                    const found = await lookupPostalCode(r?.street || form.address.street, r?.city || form.address.city);
                    if (found) { postalCode = found; postalFromMap = true; }
                  }
                  const changed = r && r.confidence >= SURE && r.changes && r.changes.length > 0;
                  // Hard rule: nothing the user typed may disappear. After the fix,
                  // every word and number of each original field must still appear
                  // somewhere; a field whose text would be lost is left exactly as
                  // the user wrote it, for them to sort out by hand.
                  const tokensOf = (t: string) => (t.toLowerCase().match(/[\p{L}\p{N}]+/gu) || []).filter(w => w.length >= 2);
                  const survives = (original: string, nextText: string): boolean => {
                    const hay = nextText.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ');
                    const hayDigits = hay.replace(/\s+/g, '');
                    return tokensOf(original).every(w => hay.includes(w) || (/^\d+$/.test(w) && hayDigits.includes(w)));
                  };
                  // Contact details found in an address field fill empty contact fields only.
                  const foundName = changed && r.contactName?.trim() && !form.contact.name.trim() ? r.contactName.trim() : '';
                  const foundPhone = changed && r.contactPhone?.trim() && !form.contact.phone.trim() ? r.contactPhone.trim() : '';
                  const foundEmail = changed && r.contactEmail?.trim() && !form.contact.email.trim() ? r.contactEmail.trim() : '';
                  const contactMoves = [foundName, foundPhone, foundEmail].filter(Boolean).length;
                  // A contact already exists, so a different name or number the model
                  // still returned belongs to the door, not the contact. Keep it visible.
                  const doorBits: string[] = [];
                  if (changed && r.contactName?.trim() && form.contact.name.trim() && r.contactName.trim().toLowerCase() !== form.contact.name.trim().toLowerCase()) doorBits.push(`Namn på dörren: ${r.contactName.trim()}`);
                  if (changed && r.contactPhone?.trim() && form.contact.phone.trim() && r.contactPhone.replace(/\D/g, '') !== form.contact.phone.replace(/\D/g, '')) doorBits.push(`Alt. tel: ${r.contactPhone.trim()}`);
                  if (changed || postalFromMap) {
                    const nextAddress = { street: (changed && r.street) || form.address.street, postalCode, city: changed ? (r.city ?? form.address.city) : form.address.city, accessCode: changed ? (r.accessCode ?? form.address.accessCode) : form.address.accessCode, accessInfo: changed ? (r.accessInfo ?? form.address.accessInfo) : form.address.accessInfo };
                    const nextContact = { name: foundName || form.contact.name, phone: foundPhone || form.contact.phone, email: foundEmail || form.contact.email };
                    const everything = [...Object.values(nextAddress), ...Object.values(nextContact), ...doorBits].join(' ');
                    let restored = 0;
                    (['street', 'postalCode', 'city', 'accessCode', 'accessInfo'] as const).forEach(k => {
                      if (form.address[k] && !survives(form.address[k], everything)) { nextAddress[k] = form.address[k]; restored++; }
                    });
                    (['name', 'phone', 'email'] as const).forEach(k => {
                      if (form.contact[k] && !survives(form.contact[k], everything)) { nextContact[k] = form.contact[k]; restored++; }
                    });
                    setForm(f => ({
                      ...f,
                      // Once a field has been parsed out, an empty answer from the model means "nothing belongs here".
                      address: { ...f.address, ...nextAddress, accessInfo: (() => { const base = nextAddress.accessInfo; const extra = doorBits.filter(b => !base.toLowerCase().includes(b.split(': ')[1].toLowerCase())); return [base, ...extra].filter(Boolean).join(', '); })() },
                      contact: { ...f.contact, ...nextContact },
                    }));
                    if (restored > 0) addNotification({ type: 'warning', message: `${restored} ${restored === 1 ? 'fält lämnades' : 'fält lämnades'} orört eftersom text annars hade försvunnit. Kontrollera och justera själv.`, duration: 6000 });
                    const n = (changed ? r.changes.length : 0) + (postalFromMap ? 1 : 0) + contactMoves;
                    addNotification({ type: 'success', message: `Adressen uppdaterad (${n} ${n === 1 ? 'ändring' : 'ändringar'}). Granska fälten och spara för att behålla.`, duration: 5000 });
                  } else {
                    addNotification({ type: 'info', message: 'Adressen ser redan rätt ut. Inget ändrades.', duration: 3500 });
                  }
                } catch (e) {
                  console.error('fixAddress failed:', e);
                  addNotification({ type: 'error', message: 'Kunde inte kontrollera adressen just nu. Försök igen om en stund.', duration: 5000 });
                } finally {
                  setAiBusy(null);
                }
              }}>Fixa adress med AI</Button>
            )}
          >
            <div className="grid grid-cols-6 gap-4">
              <Field label="Gatuadress" htmlFor="ws-street" required error={fieldErrors['address.street']} className="col-span-6">
                <Input id="ws-street" name="address.street" value={form.address.street} onChange={e => setAddress('street', e.target.value)} autoComplete="street-address" invalid={!!fieldErrors['address.street']} />
              </Field>
              <Field label="Postnummer" htmlFor="ws-postal" className="col-span-2">
                <Input id="ws-postal" name="address.postalCode" value={form.address.postalCode} onChange={e => setAddress('postalCode', e.target.value)} inputMode="numeric" autoComplete="postal-code" placeholder="851 71" className="tabular-nums" />
              </Field>
              <Field label="Ort" htmlFor="ws-city" className="col-span-4">
                <Input id="ws-city" name="address.city" value={form.address.city} onChange={e => setAddress('city', e.target.value)} autoComplete="address-level2" />
              </Field>
              <Field label="Portkod" htmlFor="ws-code" className="col-span-2">
                <Input id="ws-code" name="address.accessCode" value={form.address.accessCode} onChange={e => setAddress('accessCode', e.target.value)} placeholder="1234" className="tabular-nums" />
              </Field>
              <Field label="Tillträde" htmlFor="ws-access" hint="Våning, hiss, nycklar, var man parkerar. Hämtteamet läser detta." className="col-span-4">
                <Input id="ws-access" name="address.accessInfo" value={form.address.accessInfo} onChange={e => setAddress('accessInfo', e.target.value)} placeholder="Våning 3, hiss finns" />
              </Field>
            </div>
          </Card>

          <Card title="Kontakt">
            <div className="grid grid-cols-6 gap-4">
              <Field label="Namn" htmlFor="ws-name" hint={!form.contact.name.trim() ? 'Saknas. Projektet visas under Behöver åtgärd tills kontakt finns.' : undefined} className="col-span-6">
                <Input id="ws-name" name="contact.name" value={form.contact.name} onChange={e => setContact('name', e.target.value)} autoComplete="name" />
              </Field>
              <Field label="Telefon" htmlFor="ws-phone" className="col-span-3"
                labelAction={form.contact.phone.trim() && <a href={`tel:${form.contact.phone}`} className="inline-flex items-center gap-1 h-[32px] -my-2 px-1.5 rounded-ios-control text-[13px] text-accent-700 dark:text-accent-300 hover:bg-[var(--bg-surface-alt)]"><Phone size={12} /> Ring</a>}>
                <Input id="ws-phone" name="contact.phone" type="tel" value={form.contact.phone} onChange={e => setContact('phone', e.target.value)} autoComplete="tel" className="tabular-nums" />
              </Field>
              <Field label="E-post" htmlFor="ws-email" className="col-span-3"
                labelAction={form.contact.email.trim() && <a href={`mailto:${form.contact.email}`} className="inline-flex items-center gap-1 h-[32px] -my-2 px-1.5 rounded-ios-control text-[13px] text-accent-700 dark:text-accent-300 hover:bg-[var(--bg-surface-alt)]"><Mail size={12} /> Skriv</a>}>
                <Input id="ws-email" name="contact.email" type="email" value={form.contact.email} onChange={e => setContact('email', e.target.value)} autoComplete="email" />
              </Field>
            </div>
          </Card>

          <Card
            title="Anteckningar"
            action={form.generalNotes.trim().length > 5 && (
              <Button size="sm" variant="ghost" icon={<Sparkles size={14} />} loading={aiBusy === 'notes'} onClick={async () => {
                if (!isOnline) { addNotification({ type: 'error', message: 'Du är offline. Texten kan inte granskas nu.', duration: 4000 }); return; }
                try {
                  setAiBusy('notes');
                  const r = await polishText(form.generalNotes, 'projektanteckningar');
                  if (r && r.text && r.text !== form.generalNotes) {
                    setForm(f => ({ ...f, generalNotes: r.text }));
                    addNotification({ type: 'success', message: 'Anteckningarna språkgranskade. Spara för att behålla.', duration: 4000 });
                  } else {
                    addNotification({ type: 'info', message: 'Inga språkfel hittades.', duration: 3000 });
                  }
                } catch (e) {
                  console.error('polishText failed:', e);
                  addNotification({ type: 'error', message: 'Kunde inte språkgranska just nu. Försök igen om en stund.', duration: 5000 });
                } finally {
                  setAiBusy(null);
                }
              }}>Språkgranska</Button>
            )}
          >
            <Textarea
              name="generalNotes"
              value={form.generalNotes}
              onChange={e => setForm(f => ({ ...f, generalNotes: e.target.value }))}
              placeholder="Vad hämtteamet och kollegorna behöver veta. Nycklar, prioriteringar, önskemål från säljaren."
              rows={5}
              aria-label="Anteckningar"
            />
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-5 min-w-0">
          <Card title="Planering">
            <div className="space-y-4">
              <Field label="Status" htmlFor="ws-status" required hint={mode === 'edit' ? 'Sparas direkt när du väljer.' : undefined}>
                {mode === 'edit' ? (
                  <StatusSelect
                    value={form.status}
                    hasPickupDate={!!form.dates.scheduledPickup}
                    onNeedsPickupDate={(s) => { commitStatus(s); setPickupHint(true); pickupDateRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }}
                    onChange={commitStatus}
                    className="w-full justify-between rounded-ios-control border-[var(--border-default)] bg-[var(--bg-surface)] px-3"
                  />
                ) : (
                  <Select id="ws-status" name="status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as Project['status'] }))}>
                    {(['bostad', 'värderad', 'planerad', 'väntar', 'avbruten', 'avslutad'] as const).map(s => <option key={s} value={s}>{getStatusLabel(s)}</option>)}
                  </Select>
                )}
              </Field>

              <div className="pt-1">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">Hembesök</h3>
                  {evalPast && <span className="rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-[12px] font-medium text-amber-800 dark:text-amber-300">Passerat</span>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Värderare" htmlFor="ws-evaluator" className="col-span-2">
                    <Select id="ws-evaluator" name="evaluator" value={form.evaluator} onChange={e => setForm(f => ({ ...f, evaluator: e.target.value }))}>
                      <option value="">Välj värderare</option>
                      {EVALUATORS.map(n => <option key={n} value={n}>{n}</option>)}
                      {form.evaluator && !EVALUATORS.includes(form.evaluator) && <option value={form.evaluator}>{form.evaluator}</option>}
                    </Select>
                  </Field>
                  <Field label="Datum" htmlFor="ws-eval-date" className="col-span-2">
                    <DateField id="ws-eval-date" name="dates.evaluation" value={dateOf(form.dates.evaluation)} onChange={d => (mode === 'edit' ? commitEvaluationDate(d) : setDates({ evaluation: d ? Timestamp.fromDate(d) : null, ...(d ? {} : { evaluationStartTime: '', evaluationEndTime: '' }) }))} aria-label="Datum för hembesök" />
                  </Field>
                  <Field label="Start" htmlFor="ws-eval-start">
                    <Select id="ws-eval-start" value={form.dates.evaluationStartTime} onChange={e => setEvalStart(e.target.value)} disabled={!form.dates.evaluation}>
                      <option value="">Välj tid</option>
                      {timeOptions(evalSlots, form.dates.evaluation, form.dates.evaluationStartTime).map(o => <option key={o.t} value={o.t}>{o.label}</option>)}
                    </Select>
                  </Field>
                  <Field label="Slut" htmlFor="ws-eval-end">
                    <Select id="ws-eval-end" value={form.dates.evaluationEndTime} onChange={e => setDates({ evaluationEndTime: e.target.value })} disabled={!form.dates.evaluation}>
                      <option value="">Välj tid</option>
                      {withCurrent(form.dates.evaluationEndTime).map(t => <option key={t} value={t}>{t}</option>)}
                    </Select>
                  </Field>
                </div>
              </div>

              <div className="pt-1" ref={pickupDateRef}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-[13px] font-semibold text-[var(--text-primary)]">Hämtning</h3>
                  {pickupPast && <span className="rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-[12px] font-medium text-amber-800 dark:text-amber-300">Passerat</span>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Datum" htmlFor="ws-pickup-date" className={`col-span-2 ${pickupHint ? 'rounded-ios-control ring-2 ring-[var(--ring-accent)] p-1 -m-1' : ''}`}
                    hint={pickupHint ? 'Hämtningsdatum saknas. Sätt ett så flyttas projektet till Hämtning planerad.' : undefined}>
                    <DateField id="ws-pickup-date" name="dates.scheduledPickup" value={dateOf(form.dates.scheduledPickup)} onChange={mode === 'edit' ? commitPickupDate : setPickupDate} aria-label="Datum för hämtning" />
                  </Field>
                  <Field label="Tid" htmlFor="ws-pickup-time" className="col-span-2">
                    <Select id="ws-pickup-time" value={form.dates.scheduledPickupTime} onChange={e => setDates({ scheduledPickupTime: e.target.value })} disabled={!form.dates.scheduledPickup}>
                      <option value="">Välj tid</option>
                      {timeOptions(pickupSlots, form.dates.scheduledPickup, form.dates.scheduledPickupTime).map(o => <option key={o.t} value={o.t}>{o.label}</option>)}
                    </Select>
                  </Field>
                  <Field label="Hämtteam" htmlFor="ws-team" className="col-span-2">
                    {form.pickupTeam.length > 0 && (
                      <ul className="flex flex-wrap gap-1.5 mb-2" aria-label="Valda teammedlemmar">
                        {form.pickupTeam.map(m => (
                          <li key={m} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-900/20 pl-2.5 pr-0.5 h-[32px] text-[13px] text-emerald-800 dark:text-emerald-300">
                            {m}
                            <button type="button" aria-label={`Ta bort ${m} från teamet`} onClick={() => setForm(f => ({ ...f, pickupTeam: f.pickupTeam.filter(x => x !== m) }))} className="h-[32px] w-[32px] flex items-center justify-center rounded-full hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                              <X size={12} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <Select id="ws-team" value="" onChange={e => addMember(e.target.value)} aria-label="Lägg till teammedlem">
                        <option value="">Lägg till…</option>
                        {TEAM_MEMBERS.filter(m => !form.pickupTeam.includes(m)).map(m => <option key={m} value={m}>{m}</option>)}
                      </Select>
                      <Input value={customMember} onChange={e => setCustomMember(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addMember(customMember); } }} placeholder="Skriv namn, tryck Enter" aria-label="Annan teammedlem" />
                    </div>
                  </Field>
                </div>
              </div>

              <p className="flex items-start gap-2 rounded-ios-control bg-[var(--bg-primary)] px-3 py-2.5 text-[13px] leading-snug text-[var(--text-secondary)]">
                <Info size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>{mode === 'edit' ? 'Status och datum sparas direkt. Tider, värderare och team sparas med Spara. ' : ''}Ett hämtningsdatum flyttar projektet till <strong className="font-medium text-[var(--text-primary)]">Hämtning planerad</strong>. En vecka efter hämtningsdatumet avslutas projektet automatiskt om det fortfarande är öppet. Datum synkas till Google Kalender.</span>
              </p>
            </div>
          </Card>

          {mode === 'edit' && project && (
            <Card title="Kalender">
              <CalendarSyncStatus project={project} />
            </Card>
          )}

          {mode === 'edit' && project && (
            <Card title="Översikt">
              <ul className="-mx-2 divide-y divide-[var(--border-subtle)]">
                {[
                  { to: `/projects/${project.id}/rooms`, icon: Home, label: 'Rum & objekt', value: `${rooms.length} rum · ${itemCount} objekt` },
                  { to: `/projects/${project.id}/photos`, icon: Camera, label: 'Foton', value: `${photos.length}` },
                  ...(lens.objectCount > 0 ? [{ to: `/projects/${project.id}/photos`, icon: Sparkles, label: 'AI Lens', value: `${lens.objectCount} objekt${lensValue > 0 ? ` · ${formatSEK(lensValue)}` : ''}` }] : []),
                  { to: `/projects/${project.id}/checklists`, icon: ClipboardList, label: 'Checklista', value: checklist.total > 0 ? `${checklist.done} av ${checklist.total} klara` : 'Ingen än' },
                  { to: `/projects/${project.id}/damage-reports`, icon: AlertTriangle, label: 'Skador', value: damage.total === 0 ? 'Inga' : damage.open > 0 ? `${damage.open} öppna` : 'Alla lösta' },
                ].map(row => {
                  const Icon = row.icon;
                  return (
                    <li key={row.label}>
                      <Link to={row.to} className="flex items-center gap-3 px-2 h-11 rounded-ios-control hover:bg-[var(--bg-surface-hover)] group">
                        <Icon size={16} className="text-[var(--text-secondary)] flex-shrink-0" aria-hidden="true" />
                        <span className="flex-1 text-sm text-[var(--text-primary)]">{row.label}</span>
                        <span className="text-sm text-[var(--text-secondary)] tabular-nums">{row.value}</span>
                        <ArrowUpRight size={14} className="text-[var(--text-tertiary)] opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
        </div>
      </div>

      {mode === 'edit' && projectId && (
        <PhotoPanel projectId={projectId} rooms={rooms} photos={photos} onUploaded={refreshPhotos} pickerRef={openPicker} className="mt-5" />
      )}

      <ActionBar
        visible={dirty}
        status={mode === 'create'
          ? 'Adressen räcker för att skapa. Resten kan fyllas i senare.'
          : <span className="inline-flex items-center gap-2">{changes === 1 ? '1 fält ändrat' : `${changes} fält ändrade`}</span>}
      >
        {mode === 'create' ? (
          <Button variant="ghost" onClick={() => navigateSafely('/')}>Avbryt</Button>
        ) : (
          <Button variant="ghost" onClick={() => { setForm(initial); setFieldErrors({}); setError(null); setPickupHint(false); }}>Ångra ändringar</Button>
        )}
        <Button variant="primary" loading={saving} icon={<CheckCircle2 size={16} />} onClick={onSave}>
          {mode === 'create' ? 'Skapa projekt' : 'Spara'}
        </Button>
      </ActionBar>

      {/* Breathing room so the last card can scroll clear of the sticky bar. */}
      <div className={dirty ? 'h-16' : 'h-6'} aria-hidden="true" />
    </div>
  );
}

export default ProjectWorkspace;
